<script setup lang="ts">
import {
  AccountProtectionAccordion,
  MORE_OPTIONS_ACCORDIONS,
} from '@/types/UserLimits';

// Jogo Responsável do Minha Conta V2 (ENG-362). Rota sem frame no Figma:
// moldura no padrão V2 preservando os fluxos sensíveis de limites/pausa/
// exclusão — os componentes UserAccountProtection* antigos ficam embutidos
// nos accordions (mesma lógica de pages/user/account-protection.vue), só a
// apresentação externa (textos/botões/container) usa tokens v2-*.
// aberto como modal, a seção-alvo chega por prop (o middleware extrai da
// query da rota interceptada); a leitura de route.query fica de fallback
interface Props {
  section?: string;
}

const props = withDefaults(defineProps<Props>(), { section: '' });

const route = useRoute();
const { t } = useTexts();
const { userLimits, userIsOnTimeoutActive, userIsOnExclusionActive } =
  useUserLimits();

const { fetchUserProfile } = useUserData();
const {
  isBlockedToChange: showNextChange,
  accordionToOpen,
  forceOpenAccordion,
} = useAccountProtection();

const { isCentralizedExclusionEnabled } = useFeatureFlagsJson();
const { isAccountSetLimitsEnabled, isAccountTimeoutLimitsEnabled } =
  useFeatureFlagsNew();

const showMoreOptions = ref<boolean>(false);

function toggleMoreOptions() {
  showMoreOptions.value = !showMoreOptions.value;
}

const moreOptionsText = computed(() => {
  return showMoreOptions.value
    ? t('account.account_protection.hide_other_options')
    : t('account.account_protection.other_options');
});

const isShowMoreOptions = computed(() => {
  return (
    showMoreOptions.value ||
    !isAccountSetLimitsEnabled.value ||
    userLimits.value?.user_exclusion ||
    userLimits.value?.user_pause
  );
});

const isShowMoreButtonEnabled = computed(() => {
  return (
    isAccountSetLimitsEnabled.value &&
    !userLimits.value?.user_exclusion &&
    !userLimits.value?.user_pause
  );
});

const isCentralizedExclusionVisible = computed(() => {
  return (
    isAccountTimeoutLimitsEnabled.value && isCentralizedExclusionEnabled.value
  );
});

const getPillColor = (active: boolean) => {
  return active ? 'green' : 'gray';
};

function getPillText(active: boolean) {
  return active ? t('general.custom') : t('general.unlimited');
}

onMounted(async () => {
  const section = props.section || route.query.section;

  // deep-link ?section=exclusion (botão "Excluir conta" do ScreenAccountData
  // e da página antiga de dados) — garante a seção extra aberta mesmo que o
  // accordionToOpen já tenha expirado durante a navegação
  if (section === 'exclusion') {
    showMoreOptions.value = true;
    forceOpenAccordion(AccountProtectionAccordion.Exclusion);
  }

  // deep-link ?section=deposit (menu "Jogo Responsável" → "Limites de
  // proteção") — chega com o primeiro accordion já expandido, em vez de
  // pousar na tela com tudo fechado.
  if (section === 'deposit') {
    forceOpenAccordion(AccountProtectionAccordion.Deposit);
  }

  if (!userLimits.value?.status) {
    await fetchUserProfile({ force: true });
  }
});

// o Shell tem o próprio scroll container (overlay/painel), então o scroll da
// página antiga (scrollToTop com offset do header do site) não se aplica —
// scrollIntoView funciona em qualquer container
watch(
  accordionToOpen,
  async (newValue) => {
    if (!newValue) {
      return;
    }

    if (MORE_OPTIONS_ACCORDIONS.includes(newValue)) {
      showMoreOptions.value = true;
    }

    await sleep(200);

    if (import.meta.client) {
      document
        .getElementById(newValue)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="border-solid flex flex-col rounded-[16px] border border-v2-default/40 p-[16px]"
  >
    <div class="text-[12px] leading-[16px] text-v2-foreground/80">
      <p
        v-if="isAccountSetLimitsEnabled"
        v-html="$t('account.account_protection.text_1')"
      ></p>
      <p
        v-else
        v-html="$t('account.account_protection.text_2')"
      ></p>
    </div>

    <div
      class="border-solid mt-[16px] flex w-full flex-col border-t border-white/10"
    >
      <template v-if="isAccountSetLimitsEnabled">
        <WidgetsAccordion
          id="accordion-deposit"
          :pill-color="getPillColor(!!userLimits?.user_limit_deposit_change)"
          :pill-text="getPillText(!!userLimits?.user_limit_deposit_active)"
          :show-icon="
            showNextChange(
              userLimits?.user_limit_deposit_next_amount_change_requested_at,
            )
          "
          :title="$t('account.account_protection.user_limit_deposit')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.account_protection.text_limit_deposit')"
          />

          <UserAccountProtectionDepositLimit variant="v2" />
        </WidgetsAccordion>

        <WidgetsAccordion
          id="accordion-bet"
          :pill-color="getPillColor(!!userLimits?.user_limit_bet_change)"
          :pill-text="getPillText(!!userLimits?.user_limit_bet_active)"
          :show-icon="
            showNextChange(
              userLimits?.user_limit_bet_next_amount_change_requested_at,
            )
          "
          :title="$t('account.account_protection.user_limit_bet')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.account_protection.text_limit_bet')"
          />

          <UserAccountProtectionBetLimit variant="v2" />
        </WidgetsAccordion>

        <WidgetsAccordion
          id="accordion-loss"
          :pill-color="getPillColor(!!userLimits?.user_limit_loss_change)"
          :pill-text="getPillText(!!userLimits?.user_limit_loss_active)"
          :show-icon="
            showNextChange(
              userLimits?.user_limit_loss_next_amount_change_requested_at,
            )
          "
          :title="$t('account.account_protection.user_limit_loss')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.account_protection.text_limit_loss')"
          />

          <UserAccountProtectionLostLimit variant="v2" />
        </WidgetsAccordion>

        <WidgetsAccordion
          id="accordion-time"
          :pill-color="getPillColor(!!userLimits?.user_limit_time_change)"
          :pill-text="getPillText(!!userLimits?.user_limit_time_active)"
          :show-icon="showNextChange(userLimits?.user_limit_time_change)"
          :title="$t('account.account_protection.user_limit_time')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.account_protection.text_limit_time')"
          />

          <UserAccountProtectionTimeLimit variant="v2" />
        </WidgetsAccordion>

        <button
          v-if="isShowMoreButtonEnabled"
          class="flex items-center justify-between border-b border-white/10 px-[8px] py-[16px] text-[12px] leading-[16px] text-white/60"
          type="button"
          @click="toggleMoreOptions"
        >
          {{ moreOptionsText }}

          <NuxtIcon
            class="text-base"
            :name="showMoreOptions ? 'close' : 'arrows/angle_down'"
          />
        </button>
      </template>

      <section v-if="isShowMoreOptions">
        <WidgetsAccordion
          v-if="isAccountTimeoutLimitsEnabled"
          id="accordion-timeout"
          :pill-color="getPillColor(userIsOnTimeoutActive)"
          :pill-text="getPillText(userIsOnTimeoutActive)"
          :title="$t('account_menu.timeout_limits')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.timeout_limits.text')"
          />
          <UserAccountProtectionTimeoutLimits variant="v2" />
        </WidgetsAccordion>

        <WidgetsAccordion
          id="accordion-exclusion"
          :pill-color="getPillColor(userIsOnExclusionActive)"
          :pill-text="getPillText(userIsOnExclusionActive)"
          :title="$t('account_menu.self_exclusion')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.self_exclusion.text')"
          />
          <UserAccountProtectionSelfExclusion />
        </WidgetsAccordion>

        <WidgetsAccordion
          v-if="isCentralizedExclusionVisible"
          id="accordion-centralized-exclusion"
          :title="$t('account_menu.centralized_self_exclusion')"
        >
          <span
            class="-mt-1 mb-4 flex items-center text-[11px] leading-[16px] text-v2-default-700"
            v-html="$t('account.centralized_self_exclusion.text')"
          />
        </WidgetsAccordion>
      </section>
    </div>
  </div>
</template>
